<template>
    <div class="paper-list-dashboard animation-fadeIn">
        <div class="paper-hero-section">
            <div class="hero-content">
                <div class="hero-title-row">
                    <h1 class="hero-title">试卷资源中心</h1>
                    <div class="stat-pill">
                        <span class="stat-label">库内资源</span>
                        <span class="stat-value">{{ papersTotal }}+</span>
                    </div>
                </div>
                <p class="hero-subtitle">发现优质教育资源 • 助力智能高效组卷</p>
            </div>
            <div class="header-actions" v-if="isAdmin">
                <el-button type="primary" size="large" @click="$router.push('/admin/paper-upload')" class="action-btn">
                    <el-icon>
                        <Upload />
                    </el-icon> 智能上传
                </el-button>
                <el-button type="success" size="large" plain @click="$router.push('/admin/paper-import')"
                    class="action-btn">
                    <el-icon>
                        <DocumentAdd />
                    </el-icon> 解析导入
                </el-button>
            </div>
        </div>

        <!-- Premium Category Tabs -->
        <div class="category-tabs-container">
            <div class="category-tabs">
                <div v-for="cat in ['全部', '同步卷', '月考卷', '期中卷', '期末卷', '模拟卷', '真题卷', '强基计划']" :key="cat" class="tab-item"
                    :class="{ active: (selectedCategory || '全部') === cat }"
                    @click="selectedCategory = (cat === '全部' ? '' : cat); handleFilterChange()">
                    {{ cat }}
                </div>
            </div>
        </div>

        <!-- Streamlined Advanced Filter Bar -->
        <div class="advanced-filter-bar">
            <div class="filter-group">
                <div class="filter-item">
                    <span class="filter-label">地区</span>
                    <el-cascader v-model="filters.area" :options="pcTextArr" :props="areaCascaderProps"
                        placeholder="全部地区" clearable class="modern-cascader" @change="handleFilterChange" />
                </div>
                <div class="filter-item">
                    <span class="filter-label">年份</span>
                    <el-select v-model="filters.year" placeholder="选择年份" clearable class="modern-select"
                        @change="handleFilterChange">
                        <el-option v-for="y in years" :key="y" :label="y" :value="y" />
                    </el-select>
                </div>
            </div>

            <div class="search-group">
                <el-input v-model="filters.keyword" placeholder="快捷搜索试卷..." clearable class="modern-search"
                    @keyup.enter="handleFilterChange">
                    <template #prefix><el-icon>
                            <Search />
                        </el-icon></template>
                </el-input>
                <el-button type="primary" class="search-btn" @click="handleFilterChange">搜索</el-button>
            </div>

            <div class="view-actions">
                <div class="layout-toggle-pill">
                    <div class="toggle-icon" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'"
                        title="列表模式">
                        <el-icon>
                            <List />
                        </el-icon>
                    </div>
                    <div class="toggle-icon" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'"
                        title="宫格模式">
                        <el-icon>
                            <Grid />
                        </el-icon>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content Area with Transition -->
        <transition name="fade-transform" mode="out-in">
            <div v-if="viewMode === 'grid'" key="grid" class="papers-grid-modern" v-loading="loading">
                <el-empty v-if="papers.length === 0 && !loading" description="未找到符合条件的试卷" />
                <el-card v-for="paper in filteredPapers" :key="paper.id" shadow="hover" class="paper-modern-card"
                    @click="viewPaper(paper)">
                    <div class="p-card-header">
                        <div class="p-badge" :class="getCategoryClass(paper.category)">
                            {{ paper.category || '卷' }}
                        </div>
                        <div class="p-tags-row">
                            <el-tag v-if="paper.fileType" size="small" type="info" effect="plain">{{
                                paper.fileType.toUpperCase()
                            }}</el-tag>
                        </div>
                    </div>

                    <div class="p-card-body">
                        <h3 class="p-title" :title="paper.name">{{ paper.name }}</h3>
                        <div class="p-meta">
                            <span class="meta-item"><el-icon>
                                    <Calendar />
                                </el-icon> {{ paper.year || '-' }}</span>
                            <span class="meta-item"><el-icon>
                                    <Location />
                                </el-icon> {{ paper.region || '不限' }}</span>
                            <span class="meta-item grade-val">{{ paper.grade || '-' }}</span>
                        </div>
                        <div class="p-stats">
                            <span class="q-count">内含 <b>{{ paper.paperQuestions?.length || 0 }}</b> 题</span>
                            <span class="d-count"><el-icon>
                                    <Download />
                                </el-icon> {{ paper.downloadCount || 0 }} 次下载</span>
                        </div>
                    </div>

                    <div class="p-card-footer">
                        <el-button type="primary" link @click.stop="viewPaper(paper)"><el-icon>
                                <View />
                            </el-icon> 查看</el-button>
                        <div class="footer-spacer"></div>
                        <el-button v-if="paper.fileUrl || paper.paperQuestions?.length" type="warning" link
                            @click.stop="initiateDownload(paper)"><el-icon>
                                <Download />
                            </el-icon> 下载</el-button>
                        <el-button v-if="paper.paperQuestions?.length" type="success" link
                            @click.stop="usePaper(paper)"><el-icon>
                                <DocumentCopy />
                            </el-icon> 使用</el-button>
                        <el-button v-if="isAdmin" type="danger" link @click.stop="handleDeletePaper(paper)">
                            <el-icon>
                                <Delete />
                            </el-icon>
                        </el-button>
                        <el-button v-if="isAdmin" type="primary" link @click.stop="editPaper(paper)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                        </el-button>
                        <el-button v-if="isAdmin" :type="paper.isFeatured ? 'warning' : 'info'" link
                            @click.stop="toggleFeatured(paper)">
                            <el-icon>
                                <component :is="paper.isFeatured ? 'StarFilled' : 'Star'" />
                            </el-icon>
                        </el-button>
                    </div>
                </el-card>
            </div>

            <div v-else key="list" class="papers-list-modern" v-loading="loading">
                <el-empty v-if="papers.length === 0 && !loading" description="未找到符合条件的试卷" />
                <div v-for="paper in filteredPapers" :key="paper.id" class="paper-list-row" @click="viewPaper(paper)">
                    <div class="l-badge-col">
                        <div class="p-badge" :class="getCategoryClass(paper.category)">
                            {{ paper.category || '卷' }}
                        </div>
                    </div>
                    <div class="l-info-col">
                        <h3 class="l-title">{{ paper.name }}</h3>
                        <div class="l-meta-row">
                            <span class="meta-tag"><el-icon>
                                    <Calendar />
                                </el-icon> {{ paper.year || '-' }}</span>
                            <span class="meta-tag"><el-icon>
                                    <Location />
                                </el-icon> {{ paper.region || '-' }}</span>
                            <span class="meta-tag grade">{{ paper.grade || '-' }}</span>
                            <span class="meta-tag count">{{ paper.paperQuestions?.length || 0 }} 题</span>
                            <span class="meta-tag download-info"><el-icon>
                                    <Download />
                                </el-icon> {{ paper.downloadCount || 0 }} 下载</span>
                        </div>
                    </div>
                    <div class="l-actions-col">
                        <el-button type="primary" plain size="small" @click.stop="viewPaper(paper)">查看详情</el-button>
                        <el-button v-if="paper.fileUrl || paper.paperQuestions?.length" type="warning" plain
                            size="small" @click.stop="initiateDownload(paper)">下载</el-button>
                        <el-button v-if="paper.paperQuestions?.length" type="success" size="small"
                            @click.stop="usePaper(paper)">智能组卷</el-button>
                        <el-button v-if="isAdmin" type="primary" plain size="small"
                            @click.stop="editPaper(paper)">编辑</el-button>
                        <el-button v-if="isAdmin" type="danger" plain size="small"
                            @click.stop="handleDeletePaper(paper)">删除</el-button>
                        <el-button v-if="isAdmin" :type="paper.isFeatured ? 'warning' : 'info'" plain size="small"
                            @click.stop="toggleFeatured(paper)">
                            {{ paper.isFeatured ? '取消精选' : '设为精选' }}
                        </el-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Pagination Section -->
        <div class="pagination-container" v-if="papers.length > 0">
            <el-pagination v-model:current-page="currentPage" v-model:page-size="pageSize"
                :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper" :total="papersTotal"
                @size-change="handleSizeChange" @current-change="handleCurrentChange" background />
        </div>

        <!-- Detail Dialog -->
        <el-dialog v-model="dialogVisible" :title="currentPaper?.name" width="85%" top="4vh" class="paper-detail-dialog"
            destroy-on-close>
            <div class="paper-detail-scroll-content" v-if="currentPaper">
                <div class="d-meta-panel">
                    <el-descriptions title="试卷基础信息" :column="4" border class="modern-descriptions">
                        <el-descriptions-item label="类型">{{ currentPaper.category }}</el-descriptions-item>
                        <el-descriptions-item label="年份">{{ currentPaper.year }}</el-descriptions-item>
                        <el-descriptions-item label="地区">{{ currentPaper.region }}</el-descriptions-item>
                        <el-descriptions-item label="年级">{{ currentPaper.grade }}</el-descriptions-item>
                    </el-descriptions>
                </div>

                <!-- Image Preview Section -->
                <div class="d-preview-section" v-if="currentPaper.previewImages?.length">
                    <div class="section-label"><el-icon>
                            <Document />
                        </el-icon> 试卷原卷预览</div>
                    <div class="paper-image-gallery">
                        <div v-for="(img, idx) in currentPaper.previewImages" :key="idx" class="paper-page-wrapper">
                            <el-image :src="img" lazy :preview-src-list="currentPaper.previewImages"
                                :initial-index="idx" class="paper-page-img">
                                <template #placeholder>
                                    <div class="image-slot">加载中...</div>
                                </template>
                            </el-image>
                            <div class="page-footer">第 {{ idx + 1 }} 页 / 共 {{ currentPaper.previewImages.length }} 页
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Fallback Iframe Section (for non-converted files like PDF) -->
                <div class="d-preview-section" v-else-if="currentPaper.fileUrl && canPreviewFile(currentPaper)">
                    <div class="section-label"><el-icon>
                            <Document />
                        </el-icon> 原卷解析预览 ({{ currentPaper.fileType?.toUpperCase() }})</div>
                    <div class="preview-frame-wrapper">
                        <iframe :src="getPreviewUrl(currentPaper)" width="100%" height="600px" frameborder="0"></iframe>
                    </div>
                </div>

                <!-- Questions List Section -->
                <div class="d-questions-section" v-if="currentPaper.paperQuestions?.length">
                    <div class="section-header">
                        <div class="section-label"><el-icon>
                                <List />
                            </el-icon> 试卷题目集 ({{ currentPaper.paperQuestions.length }} 题)</div>
                        <div class="section-actions">
                            <el-button v-if="isAdmin" type="info" plain size="small"
                                @click="managePaperQuestions(currentPaper)">管理本卷题目</el-button>
                            <el-button type="primary" size="small" @click="addAllToBasket">+ 全部加入试题篮</el-button>
                        </div>
                    </div>

                    <div v-for="group in groupedQuestions" :key="group.displayName" class="q-group-block">
                        <div class="g-header">{{ group.displayName }}</div>
                        <div v-for="(pq, index) in group.questions" :key="pq.id" class="q-modern-entry">
                            <div class="q-content-wrapper">
                                <div class="q-main-body">
                                    <div class="q-content-text">
                                        <MdEditor preview-only preview-theme="github"
                                            :model-value="(getQuestionIndex(pq) + 1) + '. ' + (pq.question?.content || '')" />
                                    </div>
                                    <div class="q-options-grid" v-if="pq.question?.options?.length"
                                        :class="getLayoutClass(pq.question)">
                                        <div v-for="opt in pq.question.options" :key="opt.id" class="opt-col">
                                            <span class="o-lab">{{ opt.label }}.</span>
                                            <MdEditor preview-only preview-theme="github"
                                                :model-value="opt.content || ''" />
                                        </div>
                                    </div>
                                </div>
                                <div class="q-hover-actions">
                                    <el-button v-if="isAdmin && pq.question?.id" type="warning" link
                                        @click="editPaperQuestion(pq.question)">编辑题目</el-button>
                                    <el-button :type="isQuestionInBasket(pq.question) ? 'danger' : 'primary'" link
                                        @click="toggleQuestionInBasket(pq.question)">
                                        {{ isQuestionInBasket(pq.question) ? '移出试题篮' : '加入试题篮' }}
                                    </el-button>
                                    <el-button type="primary" link
                                        @click="openListDialog(pq.question)">收藏至题单</el-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <template #footer>
                <div class="d-footer">
                    <el-button @click="dialogVisible = false">关闭窗口</el-button>
                    <el-button v-if="currentPaper?.fileUrl" type="warning" size="large"
                        @click="initiateDownload(currentPaper)">
                        <el-icon>
                            <Download />
                        </el-icon> 下载原卷
                    </el-button>
                    <el-button type="primary" size="large" @click="usePaper(currentPaper)">使用此卷进行组卷</el-button>
                </div>
            </template>
        </el-dialog>
        <!-- Edit Basic Info Dialog -->
        <el-dialog v-model="editDialogVisible" title="编辑试卷信息" width="720px" destroy-on-close>
            <el-form :model="editForm" label-width="100px" class="modern-form">
                <el-form-item label="试卷名称">
                    <el-input v-auto-focus v-model="editForm.name" placeholder="请输入试卷名称" />
                </el-form-item>
                <el-form-item label="分类选项">
                    <el-select v-model="editForm.category" placeholder="请选择分类" style="width: 100%">
                        <el-option v-for="cat in ['同步卷', '月考卷', '期中卷', '期末卷', '模拟卷', '真题卷', '强基计划']" :key="cat" :label="cat"
                            :value="cat" />
                    </el-select>
                </el-form-item>
                <el-form-item label="考试年份">
                    <el-input v-model="editForm.year" placeholder="如：2024" />
                </el-form-item>
                <el-form-item label="所属地区">
                    <div style="display: flex; gap: 10px;">
                        <el-input v-model="editForm.region" placeholder="省份" style="flex: 1" />
                        <el-input v-model="editForm.city" placeholder="城市" style="flex: 1" />
                    </div>
                </el-form-item>
                <el-form-item label="学期信息">
                    <el-radio-group v-model="editForm.semester">
                        <el-radio label="上学期">上学期</el-radio>
                        <el-radio label="下学期">下学期</el-radio>
                        <el-radio label="全一册">全一册</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-form-item label="所属年级">
                    <el-input v-model="editForm.grade" placeholder="如：高一、高三" />
                </el-form-item>
                <el-form-item label="来源/出处">
                    <el-input v-model="editForm.source" placeholder="如：某某教育局、某某名校" />
                </el-form-item>
                <el-form-item label="原卷链接">
                    <el-input v-model="editForm.fileUrl" placeholder="文件 URL（可选）" />
                </el-form-item>
                <el-form-item label="文件类型">
                    <el-input v-model="editForm.fileType" placeholder="如：pdf / docx / zip / 7z" />
                </el-form-item>
                <el-form-item label="文件名称">
                    <el-input v-model="editForm.fileName" placeholder="上传时原始文件名（可选）" />
                </el-form-item>
                <el-form-item label="预览图链接">
                    <el-input v-model="editForm.previewImagesText" type="textarea" :rows="4"
                        placeholder="每行一个图片 URL，用于原卷预览页面" />
                </el-form-item>
                <el-form-item label="难度系数">
                    <el-select v-model="editForm.difficulty" placeholder="请选择难度" style="width: 100%">
                        <el-option label="容易" value="容易" />
                        <el-option label="普通" value="普通" />
                        <el-option label="困难" value="困难" />
                    </el-select>
                </el-form-item>
                <el-form-item label="试卷描述">
                    <el-input v-model="editForm.description" type="textarea" placeholder="请输入描述信息" />
                </el-form-item>
            </el-form>
            <template #footer>
                <div class="edit-footer">
                    <el-button @click="editDialogVisible = false">取消</el-button>
                    <el-button type="primary" :loading="saving" @click="handleUpdatePaper">保存修改</el-button>
                </div>
            </template>
        </el-dialog>

        <SelectListDialog v-model="showListDialog" :questionId="selectedQuestionForList?.id" />

        <!-- Download Payment Dialog -->
        <DownloadPayDialog v-model="payDialogVisible" :paperId="currentPaper?.id" :paperName="currentPaper?.name"
            @success="handleDownloadSuccess" />
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
    Plus, Minus, Location, Calendar, User, View, Search, List, Grid,
    DocumentCopy, Download, Document, Folder, Upload, DocumentAdd, Edit, Delete,
    Star, StarFilled
} from '@element-plus/icons-vue'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import api, { getUser, getToken } from '@/utils/api'
import { downloadFile } from '@/utils/download'
import SelectListDialog from '../components/SelectListDialog.vue'
import DownloadPayDialog from '../components/DownloadPayDialog.vue'
import { pcTextArr } from 'element-china-area-data'

const router = useRouter()
const loading = ref(false)
const papers = ref([])
const dialogVisible = ref(false)
const currentPaper = ref(null)
const selectedCategory = ref('')
const filters = reactive({ region: '', city: '', area: [], year: '', keyword: '' })
const areaCascaderProps = {
    checkStrictly: true,
    expandTrigger: 'hover',
    checkOnClickNode: true
}
const paperBasket = ref([])
const showListDialog = ref(false)
const selectedQuestionForList = ref(null)
const viewMode = ref('list')
const currentPage = ref(1)
const pageSize = ref(20)
const papersTotal = ref(0)
const editDialogVisible = ref(false)
const saving = ref(false)
const editForm = reactive({
    id: null,
    name: '',
    category: '',
    year: '',
    region: '',
    city: '',
    semester: '',
    grade: '',
    difficulty: '',
    description: '',
    source: '',
    fileUrl: '',
    fileType: '',
    fileName: '',
    previewImagesText: ''
})

// Points & Download Payment
const payDialogVisible = ref(false)
const downloadPaperId = ref(null)

const editPaper = (paper) => {
    editForm.id = paper.id
    editForm.name = paper.name
    editForm.category = paper.category
    editForm.year = paper.year
    editForm.region = paper.region
    editForm.city = paper.city
    editForm.semester = paper.semester
    editForm.grade = paper.grade
    editForm.difficulty = paper.difficulty
    editForm.description = paper.description
    editForm.source = paper.source
    editForm.fileUrl = paper.fileUrl || ''
    editForm.fileType = paper.fileType || ''
    editForm.fileName = paper.fileName || ''
    editForm.previewImagesText = Array.isArray(paper.previewImages) ? paper.previewImages.join('\n') : ''
    editDialogVisible.value = true
}

const handleUpdatePaper = async () => {
    if (!editForm.name) return ElMessage.warning('试卷名称不能为空')
    saving.value = true
    try {
        const payload = {
            id: editForm.id,
            name: editForm.name,
            category: editForm.category,
            year: editForm.year,
            region: editForm.region,
            city: editForm.city,
            semester: editForm.semester,
            grade: editForm.grade,
            difficulty: editForm.difficulty,
            description: editForm.description,
            source: editForm.source,
            fileUrl: editForm.fileUrl?.trim() || '',
            fileType: editForm.fileType?.trim() || '',
            fileName: editForm.fileName?.trim() || '',
            previewImages: (editForm.previewImagesText || '')
                .split('\n')
                .map(url => url.trim())
                .filter(Boolean)
        }
        await api.put(`/api/papers/${editForm.id}`, payload)
        ElMessage.success('更新成功')
        editDialogVisible.value = false
        fetchPapers()
    } catch (e) {
        ElMessage.error('更新失败')
    } finally {
        saving.value = false
    }
}

const handleDeletePaper = (paper) => {
    import('element-plus').then(({ ElMessageBox }) => {
        ElMessageBox.confirm(`确定要删除试卷《${paper.name}》吗？删除后不可恢复。`, '危险操作', {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
        }).then(async () => {
            try {
                await api.delete(`/api/papers/${paper.id}`)
                ElMessage.success('删除成功')
                fetchPapers()
            } catch (e) {
                ElMessage.error('删除失败')
            }
        }).catch(() => { })
    })
}

const toggleFeatured = async (paper) => {
    try {
        await api.put(`/api/papers/${paper.id}`, {
            ...paper,
            isFeatured: !paper.isFeatured
        })
        paper.isFeatured = !paper.isFeatured
        ElMessage.success(paper.isFeatured ? '已设为精选' : '已取消精选')
    } catch (e) {
        ElMessage.error('操作失败')
    }
}

const openListDialog = (q) => {
    selectedQuestionForList.value = q
    showListDialog.value = true
}

const isAdmin = computed(() => {
    const user = getUser()
    return user && (user.role === 'admin' || user.isAdmin)
})

const currentUserId = computed(() => getUser()?.id)

const regions = computed(() => Array.from(new Set(papers.value.map(p => p.region).filter(Boolean))))
const years = computed(() => {
    const cur = new Date().getFullYear()
    return Array.from({ length: 10 }, (_, i) => String(cur - i))
})

const filteredPapers = computed(() => papers.value)

const fetchPapers = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/papers', {
            params: {
                category: selectedCategory.value,
                region: filters.region,
                city: filters.city,
                year: filters.year,
                keyword: filters.keyword,
                page: currentPage.value,
                pageSize: pageSize.value
            }
        })
        papers.value = res.data.papers || []
        papersTotal.value = res.data.total || 0
    } catch (err) {
        ElMessage.error('加载试卷列表失败')
    } finally {
        loading.value = false
    }
}

const handleSizeChange = (val) => {
    pageSize.value = val
    currentPage.value = 1
    fetchPapers()
}

const handleCurrentChange = (val) => {
    currentPage.value = val
    fetchPapers()
}

const handleFilterChange = () => {
    // Update region and city from cascader area
    if (filters.area && filters.area.length > 0) {
        filters.region = filters.area[0] || ''
        filters.city = filters.area[1] || ''
    } else {
        filters.region = ''
        filters.city = ''
    }
    currentPage.value = 1
    fetchPapers()
}

const viewPaper = (paper) => {
    const routeData = router.resolve({
        name: 'PaperDetail',
        params: { id: paper.id }
    })
    window.open(routeData.href, '_blank')
}

const usePaper = (p) => router.push({ path: '/paper', query: { paperId: p.id } })

const editPaperQuestion = (question) => {
    if (!question?.id) return
    const routeData = router.resolve({
        path: '/admin/questions',
        query: { editId: String(question.id) }
    })
    window.open(routeData.href, '_blank')
}

const managePaperQuestions = (paper) => {
    if (!paper?.name) return
    const routeData = router.resolve({
        path: '/admin/questions',
        query: { keyword: paper.name }
    })
    window.open(routeData.href, '_blank')
}

const initiateDownload = (p) => {
    if (!p.id) {
        if (p.fileUrl) window.open(p.fileUrl, '_blank')
        return
    }
    currentPaper.value = p
    payDialogVisible.value = true
}

const handleDownloadSuccess = ({ downloadToken }) => {
    startActualDownload(currentPaper.value.id, downloadToken)
}

const startActualDownload = (id, token = '') => {
    let url = `/api/papers/${id}/download`
    const auth = getToken()
    if (!token && auth) {
        url += `?token=${encodeURIComponent(auth)}`
    } else if (token) {
        url += `?downloadToken=${encodeURIComponent(token)}`
    }
    downloadFile(url)
    if (currentPaper.value && currentPaper.value.id === id) {
        currentPaper.value.downloadCount = (currentPaper.value.downloadCount || 0) + 1
    }
}

const canPreviewFile = (p) => ['pdf', 'docx', 'doc'].includes(p.fileType?.toLowerCase())

const getPreviewUrl = (p) => {
    if (!p.fileUrl) return ''
    const type = p.fileType?.toLowerCase()
    if (type === 'pdf') return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(p.fileUrl)}`
    if (['docx', 'doc'].includes(type)) return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(p.fileUrl)}`
    return p.fileUrl
}

const groupedQuestions = computed(() => {
    if (!currentPaper.value?.paperQuestions) return []

    // 1. Sort by original order (1-21 sequence)
    const sorted = [...currentPaper.value.paperQuestions].sort((a, b) => a.order - b.order)

    // 2. Group by sequential appearance of types
    const groups = []
    let currentGroup = null
    let cnIdx = 0
    const cnNums = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十']

    sorted.forEach(pq => {
        const typeName = { 1: '单选题', 2: '填空题', 3: '解答题', 4: '证明题', 5: '应用题', 6: '多选题' }[pq.question?.type] || '其他'

        if (!currentGroup || currentGroup.typeName !== typeName) {
            currentGroup = {
                displayName: `${cnNums[cnIdx++]}、${typeName}`,
                typeName: typeName,
                questions: []
            }
            groups.push(currentGroup)
        }
        currentGroup.questions.push(pq)
    })

    return groups
})

const getQuestionIndex = (pq) => currentPaper.value?.paperQuestions?.findIndex(q => q.id === pq.id) || 0

const isQuestionInBasket = (q) => q && paperBasket.value.some(bq => bq.id === q.id)

const loadBasket = async () => {
    if (!currentUserId.value) return
    try {
        const res = await api.get(`/api/basket?userId=${currentUserId.value}`)
        paperBasket.value = res.data || []
    } catch (e) { }
}

const toggleQuestionInBasket = async (q) => {
    if (!q) return
    const inB = isQuestionInBasket(q)
    try {
        await api.post(`/api/basket/${inB ? 'remove' : 'add'}`, { userId: currentUserId.value, questionId: q.id })
        if (inB) paperBasket.value = paperBasket.value.filter(bq => bq.id !== q.id)
        else paperBasket.value.push(q)
        ElMessage.success(inB ? '已移除' : '已入篮')
    } catch (e) { ElMessage.error('操作失败') }
}

const addAllToBasket = async () => {
    if (!currentPaper.value?.paperQuestions) return
    let count = 0
    for (const pq of currentPaper.value.paperQuestions) {
        if (pq.question && !isQuestionInBasket(pq.question)) {
            await api.post('/api/basket/add', { userId: currentUserId.value, questionId: pq.question.id })
            paperBasket.value.push(pq.question)
            count++
        }
    }
    ElMessage.success(`成功添加 ${count} 题`)
}

const getLayoutClass = (q) => {
    const total = q.options?.reduce((s, o) => s + (o.content?.length || 0), 0) || 0
    if (total < 40) return 'layout-1-line'
    if (total < 80) return 'layout-2-line'
    return 'layout-4-line'
}

const getCategoryClass = (c) => ({
    '同步卷': 'cat-sync', '月考卷': 'cat-monthly', '期中卷': 'cat-midterm',
    '期末卷': 'cat-final', '模拟卷': 'cat-mock', '真题卷': 'cat-real',
    '强基计划': 'cat-qiangji'
}[c] || 'cat-other')

onMounted(() => {
    fetchPapers(); loadBasket()
})
</script>

<style scoped>
.paper-list-dashboard {
    padding: 48px 24px 20px;
    max-width: 1200px;
    margin: 0 auto;
}

.paper-hero-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 12px 0;
    border-bottom: 1px solid #f0f2f5;
}

.hero-title-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 4px;
}

.hero-title {
    font-size: 26px;
    font-weight: 800;
    margin: 0;
    background: linear-gradient(135deg, #1a1a1a 0%, #409eff 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    letter-spacing: -0.5px;
}

.hero-subtitle {
    font-size: 14px;
    color: #909399;
    margin: 0;
}

.hero-stats {
    display: flex;
    gap: 12px;
}

.stat-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #f0f7ff;
    padding: 6px 16px;
    border-radius: 100px;
}

.stat-label {
    font-size: 12px;
    color: #409eff;
}

.stat-value {
    font-weight: 700;
    color: #409eff;
}

.action-btn {
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
    border-radius: 10px;
    font-weight: 600;
}

/* New Premium Filter UI Styles */
.category-tabs-container {
    margin-bottom: 24px;
    border-bottom: 1px solid #f0f2f5;
}

.category-tabs {
    display: flex;
    gap: 32px;
    padding-bottom: 2px;
}

.tab-item {
    font-size: 16px;
    font-weight: 500;
    color: #606266;
    padding: 8px 4px 16px;
    cursor: pointer;
    position: relative;
    transition: all 0.3s;
}

.tab-item:hover {
    color: #409eff;
}

.tab-item.active {
    color: #409eff;
    font-weight: 700;
}

.tab-item.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: #409eff;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.4);
}

.advanced-filter-bar {
    background: #ffffff;
    padding: 10px 16px;
    border-radius: 16px;
    border: 1px solid #edf2f7;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 28px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.advanced-filter-bar:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
    border-color: #e2e8f0;
}

.filter-group {
    display: flex;
    gap: 20px;
}

.filter-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.filter-label {
    font-size: 13px;
    font-weight: 600;
    color: #909399;
    white-space: nowrap;
}

.modern-cascader {
    width: 180px;
}

.modern-select {
    width: 120px;
}

.modern-cascader :deep(.el-input__wrapper) {
    border-radius: 10px;
    background: #f8fafc;
}

.search-group {
    flex: 1;
    display: flex;
    gap: 8px;
}

.modern-select :deep(.el-input__wrapper) {
    border-radius: 10px;
    background: #f8fafc;
    box-shadow: none !important;
    border: 1px solid transparent;
    transition: all 0.2s;
}

.modern-select :deep(.el-input__wrapper:hover) {
    background: #f1f5f9;
}

.modern-search :deep(.el-input__wrapper) {
    border-radius: 100px;
    background: #f8fafc;
    box-shadow: none !important;
    padding-left: 16px;
    border: 1px solid transparent;
    transition: all 0.2s;
}

.modern-search :deep(.el-input__wrapper.is-focus) {
    background: #fff;
    border-color: #409eff;
}

.search-btn {
    border-radius: 100px;
    padding: 0 24px;
    font-weight: 600;
}

/* View Toggle Styles */
.layout-toggle-pill {
    display: flex;
    background: #f0f2f5;
    padding: 4px;
    border-radius: 100px;
    gap: 2px;
}

.toggle-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    color: #909399;
    transition: all 0.3s;
}

.toggle-icon:hover {
    color: #606266;
}

.toggle-icon.active {
    background: #fff;
    color: #409eff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.papers-grid-modern {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
}

.paper-modern-card {
    border: 1px solid #f0f0f0;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    background: #fff;
    overflow: hidden;
}

.paper-modern-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: #409eff50;
}

.p-card-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
    align-items: center;
}

.p-badge {
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.cat-sync {
    background: #67c23a;
}

.cat-monthly {
    background: #e6a23c;
}

.cat-midterm {
    background: #409eff;
}

.cat-final {
    background: #f56c6c;
}

.cat-real {
    background: #e040fb;
}

.cat-mock {
    background: #009688;
}

.cat-qiangji {
    background: #ff9800;
}

.cat-other {
    background: #909399;
}

.p-card-body {
    padding: 8px 0;
}

.p-title {
    font-size: 17px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 16px;
    height: 48px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.5;
}

.p-meta {
    display: flex;
    gap: 12px;
    font-size: 12px;
    color: #909399;
    margin-bottom: 12px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

.grade-val {
    background: #f0f2f5;
    padding: 2px 6px;
    border-radius: 4px;
}

.p-stats {
    font-size: 13px;
    color: #606266;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.p-stats b {
    color: #409eff;
    font-size: 15px;
}

.d-count {
    color: #909399;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.p-card-footer {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
}

.footer-spacer {
    flex: 1;
}

.papers-list-modern {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.paper-list-row {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    padding: 16px 24px;
    display: flex;
    align-items: center;
    transition: all 0.3s;
    cursor: pointer;
}

.paper-list-row:hover {
    border-color: #409eff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateX(4px);
}

.l-badge-col {
    flex-shrink: 0;
}

.l-info-col {
    flex: 1;
    margin: 0 24px;
}

.l-title {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 8px;
}

.l-meta-row {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
}

.meta-tag {
    font-size: 12px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 4px;
}

.meta-tag.grade {
    background: #f4f6f8;
    color: #606266;
    padding: 2px 8px;
    border-radius: 4px;
}

.meta-tag.count {
    color: #409eff;
    font-weight: 600;
}

.meta-tag.download-info {
    color: #e6a23c;
    background: #fdf6ec;
    border-radius: 4px;
    padding: 2px 8px;
}

.l-actions-col {
    display: flex;
    gap: 12px;
}

.paper-detail-scroll-content {
    max-height: 75vh;
    overflow-y: auto;
    padding-right: 8px;
}

.section-label {
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 16px;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.d-preview-section {
    margin-top: 24px;
    border-top: 1px solid #f0f0f0;
    padding-top: 24px;
}

.preview-frame-wrapper {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #ebeef5;
}

.d-questions-section {
    margin-top: 24px;
    border-top: 1px solid #f0f0f0;
    padding-top: 24px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 16px;
}

.section-actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

.q-group-block {
    margin-bottom: 32px;
}

.g-header {
    background: transparent;
    padding: 12px 0 4px 0;
    border-radius: 0;
    font-weight: 800;
    font-size: 18px;
    color: #1a1a1a;
    margin-bottom: 4px;
    border-bottom: none;
}

.q-modern-entry {
    margin-bottom: 2px;
    padding: 2px 0;
    border-radius: 0;
    transition: background-color 0.1s;
}

.q-modern-entry:hover {
    background-color: #f1f5f9;
}

.q-content-wrapper {
    position: relative;
    display: flex;
}

.q-main-body {
    flex: 1;
}

.q-hover-actions {
    position: absolute;
    top: 0;
    right: 0;
    opacity: 0;
    transition: opacity 0.2s;
    background: linear-gradient(to left, #f8fafc 70%, transparent);
    padding-left: 24px;
}

.q-modern-entry:hover .q-hover-actions {
    opacity: 1;
}

.q-content-text {
    margin-bottom: 4px;
}

.pagination-container {
    margin-top: 40px;
    display: flex;
    justify-content: center;
    background: white;
    padding: 24px;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

:deep(.el-pagination.is-background .el-pager li:not(.is-disabled).is-active) {
    background-color: #409eff;
}

/* Override MdEditor preview styles for Xueke Wang high-density look */
:deep(.md-editor-preview-wrapper) {
    padding: 0 !important;
    overflow: visible !important;
}

:deep(.md-editor-preview) {
    font-size: 16px !important;
    line-height: 1.8 !important;
    color: #1a1a1a !important;
    overflow: visible !important;
    word-break: break-all !important;
}

:deep(.md-editor-preview p) {
    margin: 0 !important;
}

:deep(.md-editor) {
    height: auto !important;
    background: transparent !important;
    border: none !important;
}

:deep(.md-editor-content) {
    overflow: visible !important;
}

:deep(.md-editor-preview > *:first-child) {
    margin-top: 0 !important;
}

.q-options-grid {
    margin-top: 12px;
}

.layout-1-line {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

.layout-2-line {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.opt-col {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.o-lab {
    font-weight: bold;
    color: #409eff;
}

/* Transitions */
.fade-transform-enter-active,
.fade-transform-leave-active {
    transition: all 0.3s ease;
}

.fade-transform-enter-from {
    opacity: 0;
    transform: translateX(-15px);
}

.fade-transform-leave-to {
    opacity: 0;
    transform: translateX(15px);
}

.animation-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.paper-image-gallery {
    background: #f0f2f5;
    padding: 40px 20px;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    max-height: 800px;
    overflow-y: auto;
}

.paper-page-wrapper {
    background: white;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    width: 100%;
    max-width: 800px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.paper-page-img {
    width: 100%;
    display: block;
}

.page-footer {
    padding: 10px;
    font-size: 12px;
    color: #909399;
    border-top: 1px solid #f0f2f5;
    width: 100%;
    text-align: center;
}

.image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    background: #f8fafc;
    color: #909399;
}
</style>
